import { Component, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgOptimizedImage } from '@angular/common';
import { ButtonDirective, TooltipDirective } from '@ps-gui/shared-core';
import { FieldGroupExtractionViewComponent } from '../../../../domains/data-capturing/field-group-extraction-view-component/field-group-extraction-view.component';
import { OcrFormInputDirective } from '../../../../directives/ocr-form-input.directive';
import { ErfassungBehandlerSections } from '../../erfassung-behandler-field-group-model';
import { PersistDataService } from '../../../../service/app-state/persist-data.service';
import { CurrentFieldService } from '../../../../service/current-field.service';
import { ExtractionField } from '../../../extraction-page/extraction-page.component';
import { ExtractionSection } from '../../../../models/domain-extraction-specifications';
import { FieldModel } from '../../../../models/extraction-models/extraction-models';
import { ExtractedValuesByKey } from '../../../../models/extracted-values';
import { BehandlerListEntry } from '../model/behandler-list-entry.interface';
import { PrefillResults } from '../../../../service/prefill.service';

@Component({
  selector: 'ct-extraction-behandler-form',
  imports: [
    FieldGroupExtractionViewComponent,
    NgOptimizedImage,
    OcrFormInputDirective,
    TooltipDirective,
    ButtonDirective,
  ],
  templateUrl: './behandler-form.component.html',
  styleUrl: './behandler-form.component.scss',
})
export class BehandlerFormComponent {
  protected readonly sections = ErfassungBehandlerSections;

  protected readonly persistDataService = inject(PersistDataService);
  private readonly currentFieldService = inject(CurrentFieldService);

  readonly currentActiveFieldModel = input.required<FieldModel | undefined>();
  readonly currentActiveField = model.required<ExtractionField | undefined>();
  readonly currentActiveSection = model.required<ExtractionSection | undefined>();

  readonly behandlerDataChanged = output<BehandlerListEntry>();
  readonly behandlerDismissed = output();

  protected readonly initData = signal<PrefillResults>({
    fields: (this.persistDataService.behandlerFormData ?? {
      fachgebiet: 'Unbekannt',
    }) as ExtractedValuesByKey,
    sources: {},
  });

  protected readonly disableConfirmDataButton = signal(true);

  constructor() {
    this.currentFieldService.behandlerErfassungComplete.pipe(takeUntilDestroyed()).subscribe(() => {
      if (!this.disableConfirmDataButton()) {
        this.onClickConfirmData();
      }
    });
  }

  protected saveFieldGroupValues(values: ExtractedValuesByKey) {
    const data = this.persistDataService.behandlerFormData ?? {};
    const newData = {
      ...data,
      ...values,
    };
    this.persistDataService.behandlerFormData = newData;

    this.disableConfirmDataButton.set(!newData['einrichtung'] && !newData['nachname']);
  }

  protected onCurrentExtractionFieldChange(section: ExtractionSection, field?: ExtractionField) {
    this.currentActiveSection.set(section);
    this.currentActiveField.set(field);
  }

  protected onClickCancel() {
    this.behandlerDismissed.emit();
  }

  protected onClickConfirmData() {
    if (this.disableConfirmDataButton()) {
      return;
    }

    const data = this.persistDataService.behandlerFormData;

    if (!data) {
      return;
    }

    this.persistDataService.behandlerFormData = {};

    this.behandlerDataChanged.emit(data);
  }
}
